/**
 * The five shipped water targets, and the BJCP-category heuristic that picks
 * one for a style.
 *
 * ## The five, and the ratio each lands on
 *
 * | Preset | Ca | Mg | Na | Cl | SO₄ | HCO₃ | SO₄:Cl | Band |
 * | --- | --- | --- | --- | --- | --- | --- | --- | --- |
 * | Balanced | 75 | 5 | 15 | 60 | 75 | 0 | 1.25 | balanced |
 * | Hoppy pale | 110 | 10 | 12 | 55 | 190 | 0 | 3.45 | hoppy |
 * | Malty amber | 90 | 10 | 25 | 110 | 65 | 60 | 0.59 | malty |
 * | Dark roasty | 100 | 15 | 35 | 90 | 65 | 160 | 0.72 | malty |
 * | Crisp lager | 45 | 5 | 8 | 45 | 60 | 0 | 1.33 | balanced |
 *
 * Three things hold across all five and are worth stating once:
 *
 * - **Calcium is never below 40 ppm.** It is not a flavour ion at these levels;
 *   it is what flocculates yeast, precipitates oxalate and drops mash pH via
 *   Kolbach. 50–150 ppm is the working range, and the crisp-lager target sits
 *   at the bottom of it deliberately, not accidentally.
 * - **Magnesium stays at or below 15 ppm.** Yeast needs a little; above ~30 ppm
 *   it reads sour and metallic. There is no target here that wants more.
 * - **Sodium stays at or below 35 ppm.** Sodium rounds malt out at 20–50 ppm
 *   and turns harsh above 100, especially against sulfate. The dark target is
 *   the only one that leans on it.
 *
 * ## Bicarbonate is the field that actually varies
 *
 * The two pale targets ask for **zero**, the amber for a little, the dark for a
 * lot. That is the whole story of mash pH in one column: alkalinity pushes the
 * mash up, roasted and crystal malt pull it down, and the target is whatever
 * balances the grist you are actually brewing. A brewer who copies a dark
 * target into a pilsner will need a lot of acid to undo it.
 *
 * ## The style heuristic
 *
 * {@link suggestTargetsForStyle} is a **documented lookup table keyed on the
 * BJCP category number**, not a model. It is deliberately crude, because a
 * suggestion that is obviously a rule of thumb invites the brewer to override
 * it, and one that looks computed does not. The UI labels it as a suggestion
 * for the same reason.
 */

import type { WaterTargetPreset } from '../../schema/water-target';

/** Every water target preset, in the order the picker shows them. */
export const waterTargetPresets: readonly WaterTargetPreset[] = [
  {
    id: 'balanced',
    name: 'Balanced',
    description:
      'A middle-of-the-road brewing water with sulfate and chloride in near-equal measure and no alkalinity at all. The right answer when the beer is not making a point about mineral character, and the safe answer when you are not sure.',
    rationale:
      'Equal-handed sulfate and chloride: neither the hops nor the malt gets the last word.',
    calciumPpm: 75,
    magnesiumPpm: 5,
    sodiumPpm: 15,
    chloridePpm: 60,
    sulfatePpm: 75,
    bicarbonatePpm: 0,
    sulfateChlorideBand: { min: 1, max: 2 },
    tags: ['balanced', 'general-purpose'],
    provenance: 'representative',
  },
  {
    id: 'hoppy-pale',
    name: 'Hoppy pale',
    description:
      'The high-sulfate water a West Coast IPA or an American pale ale is designed around. Sulfate accentuates bitterness and dries the finish; the elevated calcium keeps the mash pH down without acid and helps the yeast drop clear.',
    rationale: 'Sulfate three to four times chloride sharpens bitterness and dries the finish.',
    calciumPpm: 110,
    magnesiumPpm: 10,
    sodiumPpm: 12,
    chloridePpm: 55,
    sulfatePpm: 190,
    bicarbonatePpm: 0,
    sulfateChlorideBand: { min: 2, max: 4 },
    tags: ['pale', 'hoppy', 'ipa'],
    provenance: 'representative',
  },
  {
    id: 'malty-amber',
    name: 'Malty amber',
    description:
      'Chloride-forward water for amber and brown beers that want fullness rather than snap: bocks, altbiers, brown ales, Scottish ales. A little alkalinity is left in because a crystal-malt grist has the acidity to spend it against.',
    rationale: 'Chloride ahead of sulfate rounds the palate out and lets the malt sit forward.',
    calciumPpm: 90,
    magnesiumPpm: 10,
    sodiumPpm: 25,
    chloridePpm: 110,
    sulfatePpm: 65,
    bicarbonatePpm: 60,
    sulfateChlorideBand: { min: 0.4, max: 0.8 },
    tags: ['amber', 'malty'],
    provenance: 'representative',
  },
  {
    id: 'dark-roasty',
    name: 'Dark roasty',
    description:
      'The alkaline water a stout or a porter needs. Roasted malt is acidic enough to drive a mash below 5.2 on soft water, which reads as thin and sharp; the bicarbonate here is there to be consumed by the grist rather than to be tasted.',
    rationale:
      'Alkalinity to spend against roasted malt acidity, so the mash lands in band without help.',
    calciumPpm: 100,
    magnesiumPpm: 15,
    sodiumPpm: 35,
    chloridePpm: 90,
    sulfatePpm: 65,
    bicarbonatePpm: 160,
    sulfateChlorideBand: { min: 0.5, max: 1 },
    tags: ['dark', 'roasty', 'stout'],
    provenance: 'representative',
  },
  {
    id: 'crisp-lager',
    name: 'Crisp lager',
    description:
      'Soft, low-mineral water for pale lagers and delicate wheat beers, in the spirit of Pilsen but with enough calcium to keep the yeast and the mash pH honest. Everything is dialled down so the malt and the noble hops have nothing to compete with.',
    rationale: 'Low total mineral with a whisper of sulfate: clean, dry, and nothing in the way.',
    calciumPpm: 45,
    magnesiumPpm: 5,
    sodiumPpm: 8,
    chloridePpm: 45,
    sulfatePpm: 60,
    bicarbonatePpm: 0,
    sulfateChlorideBand: { min: 1, max: 1.5 },
    tags: ['pale', 'lager', 'soft'],
    provenance: 'representative',
  },
];

/** `id` → water target preset. */
export const waterTargetPresetsById: ReadonlyMap<string, WaterTargetPreset> = new Map(
  waterTargetPresets.map((preset) => [preset.id, preset]),
);

/**
 * BJCP 2021 category number → target preset id.
 *
 * The whole heuristic, written out so it can be argued with. Categories not
 * listed fall through to `balanced` — that is categories 27–34, the historical,
 * fruit, spiced, alternative-fermentable, wood and specialty families, where
 * the base beer decides the water and the category cannot.
 *
 * | Categories | Target | Why |
 * | --- | --- | --- |
 * | 1–5, 10, 23 | `crisp-lager` | Pale lagers, German wheat, European sours: soft water, nothing in the way. |
 * | 6–9, 14, 17, 19, 24–26, 32 | `malty-amber` | Amber and strong malty beers, Belgians, rauchbier: chloride forward, a little alkalinity. |
 * | 11, 12, 18, 21, 22 | `hoppy-pale` | Bitters, IPAs, pale and strong American ales: sulfate forward. Burton's sulfate *is* the character of a bitter, which is why category 11 sits here and not with the ambers. |
 * | 13, 15, 16, 20 | `dark-roasty` | Brown, Irish, dark British and American stouts and porters: alkalinity to spend against roast. |
 */
export const STYLE_CATEGORY_WATER_TARGETS: Readonly<Record<number, string>> = {
  1: 'crisp-lager',
  2: 'crisp-lager',
  3: 'crisp-lager',
  4: 'crisp-lager',
  5: 'crisp-lager',
  6: 'malty-amber',
  7: 'malty-amber',
  8: 'malty-amber',
  9: 'malty-amber',
  10: 'crisp-lager',
  11: 'hoppy-pale',
  12: 'hoppy-pale',
  13: 'dark-roasty',
  14: 'malty-amber',
  15: 'dark-roasty',
  16: 'dark-roasty',
  17: 'malty-amber',
  18: 'hoppy-pale',
  19: 'malty-amber',
  20: 'dark-roasty',
  21: 'hoppy-pale',
  22: 'hoppy-pale',
  23: 'crisp-lager',
  24: 'malty-amber',
  25: 'malty-amber',
  26: 'malty-amber',
  32: 'malty-amber',
};

/** The target every unmapped category falls back to. */
export const DEFAULT_WATER_TARGET_ID = 'balanced';

/**
 * The water target one BJCP category suggests.
 *
 * A **suggestion, not a prescription** — see {@link STYLE_CATEGORY_WATER_TARGETS}
 * for the whole table and its reasoning.
 *
 * Takes a category number rather than a style id so this module stays free of
 * any dependency on the style collection; `suggestTargetsForStyle` in
 * `catalog.ts` is the style-id-shaped wrapper, and lives there because that is
 * where the two collections already meet.
 *
 * @param categoryNumber - BJCP 2021 category, 1–34.
 * @returns The suggested preset. Never `undefined`: an unmapped category falls
 *   back to `balanced`, which is a real answer rather than a shrug.
 */
export function suggestTargetForCategory(categoryNumber: number): WaterTargetPreset {
  const id = STYLE_CATEGORY_WATER_TARGETS[categoryNumber] ?? DEFAULT_WATER_TARGET_ID;
  // The table and the fallback are both ids from this file's own collection, so
  // the lookup cannot miss; the assertion documents that rather than hiding it.
  const preset = waterTargetPresetsById.get(id);
  if (preset === undefined) throw new Error(`Water target preset "${id}" is not in the catalog.`);
  return preset;
}
